'use client';

import { useEffect, useRef, useState } from 'react';

import { useAtom } from 'jotai';
import { z } from 'zod';

import { GetProjectPreviewQuery } from '@/manager-graphql';
import { isUrlQuestionText } from '@/shared-project';
import {
  ActivityStore,
  iFrameUrlAtom,
  messageFormSchema,
  useProjectResponseModeContext,
  getOrCreateShuffledOptions,
} from '@/shared-survey';
import { ensureUrlProtocol, navigateTopLevel } from '@/shared-utilities';
import {
  InitiateSurveyV3Mutation,
  MessageSender,
  MessageV2,
  OptionRenderType,
} from '@/survey-graphql';

import { useProjectPreviewHandler } from './project-response-preview-handler.hooks';
import { useProjectPreviewMessages } from './project-response-preview-messages.hooks';
import { useProjectPreviewQueries } from './project-response-preview-queries.hooks';
import {
  initializeIframeUrlIfUnset,
  requestIntroTts,
  stopListeningWhenConversationOver,
} from './project-response-shared-effects.helpers';
import { useProjectResponseTTS } from './project-response-tts.hooks';
import { NIL_UUID } from './project-response.consts';
import {
  getQuestionType,
  isOptionRenderType,
} from './project-response.helpers';
import {
  resolveLegacyUrlQuestionText,
  resolveLegacyUrlQuestionTranscript,
} from './survey-project-response-url-question-text.helpers';

export type MessageFormValues = z.infer<typeof messageFormSchema>;

// Helper function to detect if a question is a URL question
export const isUrlQuestion = (question: {
  text?: string;
  contentUrl?: string | null;
}): boolean => {
  return isUrlQuestionText(question.text) && Boolean(question.contentUrl);
};

function getOptionRenderType(value?: string): OptionRenderType {
  if (isOptionRenderType(value)) {
    return value;
  }
  return OptionRenderType.Text;
}

interface UseProjectPreviewProps {
  project: GetProjectPreviewQuery['project'];
  organization?: {
    id: string;
  };
  sessionId: string;
  firstQuestion: InitiateSurveyV3Mutation['initiateSurveyV3']['firstQuestion'];
}

export function useProjectPreview({
  project,
  sessionId,
  firstQuestion,
  organization,
}: UseProjectPreviewProps) {
  const activityStore = useRef<ActivityStore>({
    lastContentUpdateAt: null,
    notifyContentUpdate() {
      this.lastContentUpdateAt = Date.now();
    },
  });

  const [messages, setMessages] = useState<MessageV2[]>([]);

  const [contentUrl, setIFrameUrl] = useAtom(iFrameUrlAtom);

  const { stopListening } = useProjectResponseModeContext();
  const {
    getSpeech,
    getSpeechForMessages,
    speechAudios,
    replaceMessageId,
    audioQueue,
  } = useProjectResponseTTS(
    project.settings?.isAiVoiceEnabled ?? false,
    project.id
  );
  const { serverTimeData, pollResultData, isPollResultLoading } =
    useProjectPreviewQueries({ project });

  const isPublished = true;

  // Get current server time synchronously (from cache or fallback to local time)
  const getServerTime = (): number => {
    if (serverTimeData?.serverTime) {
      return new Date(serverTimeData.serverTime).getTime();
    }
    const lastMessage = messages[messages.length - 1];
    if (lastMessage) {
      return new Date(lastMessage.createdAt).getTime() + 1000;
    }
    return Date.now();
  };

  const initialMessageFromProject: MessageV2 = (() => {
    const filtered =
      firstQuestion?.options?.filter(
        (o): o is NonNullable<typeof o> =>
          o != null &&
          typeof o.value === 'string' &&
          typeof o.label === 'string'
      ) ?? [];
    const optionsResult =
      filtered.length > 0
        ? getOrCreateShuffledOptions(
            sessionId,
            firstQuestion.id,
            filtered,
            firstQuestion?.randomize ?? false
          )
        : undefined;
    return {
      id: NIL_UUID,
      text: resolveLegacyUrlQuestionText(firstQuestion?.text, project.language),
      createdAt: new Date(1).toISOString(),
      sender: MessageSender.Assistant,
      optionRenderType: getOptionRenderType(firstQuestion?.optionRenderType),
      transcript:
        resolveLegacyUrlQuestionTranscript(
          firstQuestion?.text,
          firstQuestion?.textForVoice,
          project.language
        ) ?? '',
      assets: firstQuestion?.assets?.map((a) => ({
        id: a.id,
        name: a.name,
        url: a.url,
        description: a.description,
        createdAt: a.createdAt,
        updatedAt: a.updatedAt,
      })),
      contentUrl: firstQuestion?.contentUrl,
      metadata: {
        type: getQuestionType(firstQuestion?.type ?? ''),
        options: optionsResult,
        isConversationOver: false,
        required: firstQuestion?.required ?? false,
        maxSelections: firstQuestion?.maxSelections ?? null,
      },
    };
  })();

  const {
    handleAddMessage,
    isSaving,
    isSubmitting,
    formatTranscriptAnswerLoading,
    progress,
    replyFeedbackIds,
  } = useProjectPreviewHandler({
    project,
    sessionId,
    firstQuestion,
    organization,
    initialMessageFromProject,
    messages,
    setMessages,
    setIFrameUrl,
    getServerTime,
    speechAudiosData: {
      getSpeech,
      getSpeechForMessages,
      speechAudios,
      replaceMessageId,
      audioQueue,
    },
    activityStore,
  });

  const { messages: sortedMessages } = useProjectPreviewMessages({
    firstQuestion,
    project,
    isPollResultLoading,
    messages,
    initialMessageFromProject,
    formatTranscriptAnswerLoading,
    speechAudios,
    pollResultData,
  });

  const isConversationOver = !!(
    messages.length &&
    messages[messages.length - 1].metadata?.isConversationOver
  );

  const handleSubmit = async (values: MessageFormValues) => {
    await handleAddMessage(values);
    activityStore.current.lastContentUpdateAt = null;
  };

  const handleConversationOver = project.redirectUrl
    ? async () => {
        const url = project.redirectUrl;
        if (isConversationOver && typeof url === 'string') {
          navigateTopLevel(ensureUrlProtocol(url));
        }
      }
    : undefined;

  useEffect(
    () =>
      initializeIframeUrlIfUnset({
        current: contentUrl,
        candidate: firstQuestion?.contentUrl,
        set: setIFrameUrl,
      }),
    [contentUrl, firstQuestion, setIFrameUrl]
  );

  // Stop listening when conversation is over
  useEffect(
    () =>
      stopListeningWhenConversationOver({ isConversationOver, stopListening }),
    [isConversationOver, stopListening]
  );

  useEffect(
    () =>
      requestIntroTts({
        transcript: initialMessageFromProject.transcript,
        messageId: NIL_UUID,
        getSpeech,
      }),
    [getSpeech, initialMessageFromProject.transcript]
  );

  return {
    isPublished,
    isSaving,
    isSubmitting,
    messages: sortedMessages,
    handleSubmit,
    insightLoading: false,
    isConversationOver,
    progress,
    handleConversationOver,
    activityStore,
    contentUrl,
    setIFrameUrl,
    replyFeedbackIds,
  };
}
